import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../../dateFilter'
import { useGroupFilter } from '../../../../groupFilter'
import { useVariables } from '../../../../variables'
import { IApiDataType } from '../../../types'

interface IDataType {
  listLocationDeliveryStaffKpi: {
    nodes: {
      locationId: number
      employeeInfo: {
        id: number
        firstName: string
        lastName: string
      }
      employeeRoleName: string
      totalOrderCount: number
      inaccurateOrderPercent: number
      cancelledOrderPercent: number
      delayedOrderPercent: number
      avgCustomerReviewScore: number
      totalReviewCount: number
    }[]
  }
}

const query = gql`
  query devlieryStaff(
    $iEndDate: Date!
    $iStartDate: Date!
    $iLocationId: Int!
  ) {
    listLocationDeliveryStaffKpi(
      iLocationId: $iLocationId
      iStartDate: $iStartDate
      iEndDate: $iEndDate
    ) {
      nodes {
        locationId
        employeeInfo {
          id
          firstName: preferredName
          lastName: familyName
        }
        employeeRoleName
        totalOrderCount
        inaccurateOrderPercent
        cancelledOrderPercent
        delayedOrderPercent
        avgCustomerReviewScore
        totalReviewCount
      }
    }
  }
`

export const bbbDeliveryStaffConfigs = {
  rowIndex: 'string',
  employeeName: 'string',
  employeeRoleName: 'string',
  totalOrderCount: 'number',
  inaccurateOrderPercent: 'percent',
  cancelledOrderPercent: 'percent',
  delayedOrderPercent: 'percent',
  avgCustomerReviewScore: 'number',
} as const

const YESTERDAY = moment().subtract(1, 'day')

const useBbbDeliveryStaff = () => {
  const { variables } = useVariables()
  const { startDate, endDate: filterEndDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()
  const endDate = useMemo(
    () =>
      moment(filterEndDate, 'YYYY-MM-DD').isAfter(YESTERDAY)
        ? YESTERDAY.format('YYYY-MM-DD')
        : filterEndDate,
    [filterEndDate],
  )
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iLocationId: groupFilter?.ids[0],
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const deliveryStaff = data?.listLocationDeliveryStaffKpi.nodes

      if (!deliveryStaff) return null

      const sortingKey = (() => {
        switch (
          variables.selectOptions?.deliveryKpiType.value.slice(-1)[0]?.[0]
        ) {
          case 'orderWithAccuracyIssuePercent':
            return 'inaccurateOrderPercent'
          case 'orderWithCancellationsIssuePercent':
            return 'cancelledOrderPercent'
          case 'orderWithLatenessIssuePercent':
            return 'delayedOrderPercent'
          case 'combinedStarRating':
          default:
            return 'avgCustomerReviewScore'
        }
      })()

      return deliveryStaff
        .map((d) => ({
          ...d,
          employeeName: [d.employeeInfo.firstName, d.employeeInfo.lastName]
            .filter(Boolean)
            .join(' '),
          id: d.employeeInfo.id.toString(),
          parentId: 'root',
        }))
        .sort((a, b) => {
          const valueA = a[sortingKey] as number
          const valueB = b[sortingKey] as number

          return valueB - valueA
        })
    }, [data, variables]),
    loading,
  }
}

export default useBbbDeliveryStaff
